import { cn } from '@costar-ui/cosmos'
import { createColumnHelper } from '@tanstack/react-table'
import type {
  CellContext,
  ColumnDef,
  HeaderContext
} from '@tanstack/react-table'

import type { CoreTableProps, GridColumn } from './types'

type BuildColumnsDefArgs<TDataRow> = CoreTableProps<TDataRow> & {
  isMobile: boolean
}
type Sorted<TDataRow> = {
  left: GridColumn<TDataRow>[]
  right: GridColumn<TDataRow>[]
  rest: GridColumn<TDataRow>[]
}

export function sortByFixed<TDataRow>(
  columnDef: GridColumn<TDataRow>[]
): Sorted<TDataRow> {
  const columns = columnDef.reduce(
    (acc, cur) => {
      if (cur.fixed === 'left') {
        acc.left.push(cur)
      } else if (cur.fixed === 'right') {
        acc.right.push(cur)
      } else {
        acc.rest.push(cur)
      }
      return acc
    },
    {
      left: [],
      right: [],
      rest: []
    } as Sorted<TDataRow>
  )
  return columns
  // return []
  //   .concat(columns.left)
  //   .concat(columns.rest)
  //   .concat(columns.right) as GridColumn<TDataRow>[]
  /*
  const columns = columnDef.sort((a, b) => {
    console.log({ a, b })
    //put 'left' fixed columns on the top
    if (b.fixed === 'left') {
      return -1
    }

    //put 'right' fixed columns on the bottom
    if (a.fixed === 'right') {
      return 1
    }

    //rest as it is
    return 0
  })
  */
}

export function buildColumnsDef<TDataRow = Record<string, unknown>>({
  columnDef,
  sortingProps,
  fillContainer,
  isMobile
}: BuildColumnsDefArgs<TDataRow>): {
  columns: ColumnDef<TDataRow>[]
  leftStickColumns: string[]
} {
  const columnHelper = createColumnHelper<TDataRow>()

  const { left, rest, right } = sortByFixed(columnDef)
  const leftCount = left.length
  const columns = ([...left, ...rest, ...right] as GridColumn<TDataRow>[]).map(
    (c: GridColumn<TDataRow>, i: number) => {
      const leftLastColumn = leftCount - 1 === i
      const sorting = sortingProps?.sorting
      const className = cn(
        c.ellipsis ? 'w-full max-w-0 truncate' : '',
        c.fixed === 'left'
          ? {
              'sticky left-0 csg-tw-table-cell-fix-left-last': leftCount === 1,
              'sticky csg-tw-table-cell-fix-left-last': leftLastColumn,
              'sticky left-0': !leftLastColumn
            }
          : '',
        {
          'text-center': c.align === 'center',
          'text-left': c.align === 'left',
          'text-right': c.align === 'right'
        },
        c.className
      )
      return columnHelper.accessor(c.dataField, {
        id: c.dataField as string,
        cell: (props: CellContext<TDataRow, React.ReactNode>) => {
          const value = props.getValue()
          return c.cell
            ? c.cell({ row: props.cell.row.original, value })
            : value
        },
        header: (props: HeaderContext<TDataRow, React.ReactNode>) =>
          c.header(props),
        meta: {
          className,
          headerClassName: cn(
            {
              'text-center': c.align === 'center',
              'text-left': c.align === 'left',
              'text-right': c.align === 'right'
            },
            c.headerClassName
          )
        },
        size: c.size,
        minSize: c.minSize,
        maxSize: c.maxSize,
        enableResizing: c.enableResizing !== false,
        enableSorting:
          sorting && c.enableSorting === true
            ? true
            : false /** We force tanstack to disable sort by default. see {@link CoreTableProps}*/
      }) as ColumnDef<TDataRow>
    }
  )

  // Add dummy column if fillContainer type is 'append'
  // this column will be resized to fill the entire parent container
  if (fillContainer === 'append' && !isMobile) {
    columns.push(
      columnHelper.display({
        id: 'fillContainerColumn',
        meta: {
          className: 'w-full',
          headerClassName: 'w-full'
        },
        enableResizing: false,
        cell: () => null,
        header: () => null
      })
    )
  }

  return { columns, leftStickColumns: left.map(c => c.dataField as string) }
}
